import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../components/api';

const DEFAULT_CONFIG = {
  provider: 'manual', // 'manual' | 'wati' | 'twilio' | 'aisensy' | 'meta'
  watiUrl: '',        // e.g. https://live-server.wati.io
  watiToken: '',      // WATI bearer token
  twilioSid: '',      // Twilio Account SID
  twilioToken: '',    // Twilio Auth Token
  twilioFrom: '',     // Twilio WhatsApp number (digits only, e.g. 14155238886)
  aisensyApiKey: '',       // AiSensy API key
  aisensyUserName: '',     // AiSensy account user name
  aisensyCampaignName: '', // AiSensy template/campaign name
  metaPhoneNumberId: '',   // Meta WhatsApp Business phone number ID
  metaAccessToken: '',     // Meta Cloud API access token
  metaAppSecret: '',       // Meta App Secret (for appsecret_proof)
  metaTemplateName: '',             // Meta template name (leave blank for plain text)
  metaTemplateLang: 'en_US',      // Template language code
};

const WhatsAppContext = createContext(null);

export function WhatsAppProvider({ children }) {
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [configOpen, setConfigOpen] = useState(false);

  // Load config from backend — only when authenticated (token exists).
  // Without this guard, the API call fires on the login page (WhatsAppProvider
  // wraps the entire app) → 401 → interceptor alert storm.
  // Re-fetches after login when finishStaffLogin does window.location or navigate
  // (full page reload re-mounts, sessionStorage now has token → fetch fires).
  const fetchedRef = useRef(false);
  useEffect(() => {
    // Check if user is logged in (localStorage auth hints or old sessionStorage token)
    var authHints = (function() { try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; } })();
    var hasAuth = authHints.username || sessionStorage.getItem('token');
    if (!hasAuth || fetchedRef.current) return;
    fetchedRef.current = true;
    api.get(`${process.env.REACT_APP_URL}/orgs/whatsapp-config`)
      .then(({ data }) => {
        const merged = { ...DEFAULT_CONFIG, ...data };
        if (!merged.provider) merged.provider = 'manual';
        setConfig(merged);
      })
      .catch(() => {});
  }, []);

  const saveConfig = useCallback(async (newConfig) => {
    const merged = { ...DEFAULT_CONFIG, ...newConfig };
    setConfig(merged);

    try {
      await api.put(`${process.env.REACT_APP_URL}/orgs/whatsapp-config`, merged);
    } catch (err) {
      console.error('[WhatsApp] Failed to save config to backend:', err);
    }
  }, []);

  /**
   * sendWhatsApp(phone, message)
   *  - manual  → opens wa.me link in new tab
   *  - wati    → POST /api/whatsapp/send (backend proxies to WATI)
   *  - twilio  → POST /api/whatsapp/send (backend proxies to Twilio)
   * Returns { success, provider, error? }
   */
  const sendWhatsApp = useCallback(async (phone, message) => {
    const clean = String(phone).replace(/\D/g, '');
    const e164 = clean.startsWith('91') ? clean : `91${clean}`;

    if (config.provider === 'manual') {
      window.open(`https://wa.me/${e164}?text=${encodeURIComponent(message)}`, '_blank');
      return { success: true, provider: 'manual' };
    }

    try {
      await api.post(`${process.env.REACT_APP_URL}/whatsapp/send`, {
        to: e164,
        message,
        provider: config.provider,
        watiUrl: config.watiUrl,
        watiToken: config.watiToken,
        twilioSid: config.twilioSid,
        twilioToken: config.twilioToken,
        twilioFrom: config.twilioFrom,
        aisensyApiKey: config.aisensyApiKey,
        aisensyUserName: config.aisensyUserName,
        aisensyCampaignName: config.aisensyCampaignName,
        metaPhoneNumberId: config.metaPhoneNumberId,
        metaAccessToken: config.metaAccessToken,
        metaAppSecret: config.metaAppSecret,
        metaTemplateName: config.metaTemplateName,
        metaTemplateLang: config.metaTemplateLang,
      });
      return { success: true, provider: config.provider };
    } catch (err) {
      console.error('[WhatsApp] Send failed, falling back to manual:', err);
      // Graceful fallback — always open wa.me so message is never lost
      window.open(`https://wa.me/${e164}?text=${encodeURIComponent(message)}`, '_blank');
      return { success: false, provider: config.provider, error: err.message };
    }
  }, [config]);

  return (
    <WhatsAppContext.Provider value={{ config, saveConfig, sendWhatsApp, configOpen, setConfigOpen }}>
      {children}
    </WhatsAppContext.Provider>
  );
}

export function useWhatsApp() {
  const ctx = useContext(WhatsAppContext);
  if (!ctx) throw new Error('useWhatsApp must be used inside WhatsAppProvider');
  return ctx;
}
